import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer';
import ProfileTagsCollapse from '../../components/ProfileTagsCollapse/ProfileTagsCollapse';
import { resolveAvatarUrl, withAvatarFallback } from '../../utils/avatar';
import { API_BASE } from '../../config';
import useOnboardingGuard from '../../hooks/useOnboardingGuard';

export default function UserProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { loading: onboardingLoading } = useOnboardingGuard();
  const photosContainerRef = useRef(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [photoRatios, setPhotoRatios] = useState([]);
  const [firstRowPhotoCount, setFirstRowPhotoCount] = useState(1);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isDescriptionOverflowing, setIsDescriptionOverflowing] = useState(false);
  const descriptionRef = useRef(null);

  const [lightbox, setLightbox] = useState({
    isOpen: false,
    images: [],
    currentIndex: 0
  });

  const openLightbox = (images, index = 0) => {
    setLightbox({
      isOpen: true,
      images: images || [],
      currentIndex: index
    });
  };

  const closeLightbox = () => {
    setLightbox({
      isOpen: false,
      images: [],
      currentIndex: 0
    });
  };

  const nextLightboxImage = () => {
    setLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % prev.images.length
    }));
  };

  const prevLightboxImage = () => {
    setLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length
    }));
  };

  useEffect(() => {
    let isActive = true;

    setLoading(true);
    setError(null);
    setUserData(null);
    setShowAllPhotos(false);
    setIsDescriptionExpanded(false);
    setIsDescriptionOverflowing(false);

    const fetchProfile = async () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/log_in');
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/api/Profile/by-id/${userId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/log_in');
          return;
        }

        if (response.status === 404) {
             throw new Error('User not found');
        }

        if (!response.ok) {
          throw new Error('Failed to fetch profile');
        }

        const data = await response.json();
        if (isActive) {
          setUserData(data);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        if (isActive) {
          setError(error.message);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    if (userId) {
        fetchProfile();
    }

    return () => {
      isActive = false;
    };
  }, [userId, navigate]);

  const { 
      firstName, 
      lastName, 
      nickname, 
      bio, 
      city, 
      jobTitle, 
      birthDate, // Add birthDate
      avatarUrl, 
      friendsCount, 
      onboarding,
      photos
  } = userData || {};

  // Extract interests from onboarding data
  const interests = (onboarding || [])
    .flatMap(q => q.selected?.map(s => s.text) || [])
    .filter(text => text && text.toLowerCase() !== 'другое' && !text.toLowerCase().startsWith('option #'));
  
  // Prepare photos
  const userPhotos = useMemo(
    () => (photos || []).map(p => resolveAvatarUrl(p.url || p.Url || p)).filter(Boolean),
    [photos]
  );
  const hasPhotosSection = userPhotos.length > 1;

  const calculateFirstRowPhotoCount = useCallback(() => {
    const container = photosContainerRef.current;
    if (!container || userPhotos.length === 0) {
      setFirstRowPhotoCount(1);
      return;
    }

    const viewportWidth = window.innerWidth || 1920;
    const isMobile = viewportWidth <= 767.84;
    const scaleWidth = isMobile ? viewportWidth : Math.min(viewportWidth, 2560);
    const photoHeight = scaleWidth * (isMobile ? 81.69 / 360 : 140 / 1920);
    const photoGap = scaleWidth * (isMobile ? 5.84 / 360 : 20 / 1920);
    const containerWidth = container.clientWidth;
    let usedWidth = 0;
    let count = 0;

    for (let index = 0; index < userPhotos.length; index += 1) {
      const ratio = photoRatios[index] || 1;
      const nextWidth = photoHeight * ratio;
      const nextUsedWidth = usedWidth + (count > 0 ? photoGap : 0) + nextWidth;
      if (nextUsedWidth > containerWidth && count > 0) break;
      usedWidth = nextUsedWidth;
      count += 1;
    }

    setFirstRowPhotoCount(Math.max(1, count));
  }, [userPhotos, photoRatios]);

  useEffect(() => {
    setShowAllPhotos(false);
    setPhotoRatios(Array(userPhotos.length).fill(1));

    userPhotos.forEach((photoUrl, index) => {
      const image = new Image();
      image.onload = () => {
        const ratio = image.naturalWidth && image.naturalHeight
          ? image.naturalWidth / image.naturalHeight
          : 1;
        setPhotoRatios((current) => {
          const next = [...current];
          next[index] = ratio;
          return next;
        });
      };
      image.src = photoUrl;
    });
  }, [userPhotos]);

  useEffect(() => {
    calculateFirstRowPhotoCount();

    const container = photosContainerRef.current;
    const resizeObserver = container && 'ResizeObserver' in window
      ? new ResizeObserver(calculateFirstRowPhotoCount)
      : null;
    resizeObserver?.observe(container);
    window.addEventListener('resize', calculateFirstRowPhotoCount);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', calculateFirstRowPhotoCount);
    };
  }, [calculateFirstRowPhotoCount]);

  if (loading) {
    return <div className="profile-page profile-page--loading">Загрузка...</div>;
  }

  if (error) {
    return (
        <div className="profile-page profile-page--loading">
            <div className="text-red-500 text-xl">{error}</div>
            <button onClick={() => navigate(-1)} className="profile-page__back profile-page__back-button">Назад</button>
        </div>
    );
  }

  if (!userData) return null;

  const fullName = [firstName, lastName].filter(Boolean).join(' ') || 'Имя Фамилия';
  const userNickname = (nickname || userData.username || '').startsWith('@')
    ? (nickname || userData.username)
    : (nickname || userData.username ? `@${nickname || userData.username}` : '@username');
  const shownBirthDate = birthDate ? new Date(birthDate).toLocaleDateString('ru-RU') : '—';
  const shownDescription = bio || userData.description || '';
  const visiblePhotoCount = showAllPhotos
    ? userPhotos.length
    : Math.min(userPhotos.length, firstRowPhotoCount);
  const visiblePhotos = userPhotos.slice(0, visiblePhotoCount);
  const hiddenPhotosCount = Math.max(0, userPhotos.length - visiblePhotoCount);
  const morePhotosLabelCount = hiddenPhotosCount + 1;

  return (
    <div className="profile-page user-profile-page">
      
      <main className="profile-page__canvas">

        <section className="profile-layout user-profile-layout" aria-label="Профиль пользователя">
          <aside className={`profile-card user-profile-card ${hasPhotosSection ? 'user-profile-card--with-photos' : 'user-profile-card--compact'}`}>
            <div className="profile-card__avatar-wrap" onClick={() => openLightbox([resolveAvatarUrl(avatarUrl)], 0)} style={{cursor: 'pointer'}}>
              <img 
                src={resolveAvatarUrl(avatarUrl)} 
                alt={fullName} 
                className="profile-card__avatar"
                onError={withAvatarFallback()}
              />
            </div>

            <div className="profile-card__name-block-mobile">
              <h1 className="profile-card__name-mobile">{fullName}</h1>
              <p className="profile-card__nickname-mobile">{userNickname}</p>
            </div>

            <dl className="profile-card__meta user-profile-card__meta">
              <div>
                <dt>Дата рождения</dt>
                <dd>{shownBirthDate}</dd>
              </div>
              <div>
                <dt>Профессия</dt>
                <dd>{jobTitle || userData.profession || '—'}</dd>
              </div>
              <div>
                <dt>Город</dt>
                <dd>{city || '—'}</dd>
              </div>
              <div>
                <dt>Единомышленники</dt>
                <dd>{friendsCount ?? 0}</dd>
              </div>
            </dl>
          </aside>

          <section className="profile-details user-profile-details">
            <header className="profile-details__header">
              <h1>{fullName}</h1>
              <p>{userNickname}</p>
            </header>

            <section className="profile-details__section">
              <h2>Описание</h2>
              <div 
                className={`profile-details__about ${isDescriptionOverflowing ? 'is-overflowing' : ''} ${isDescriptionExpanded ? 'is-expanded' : ''}`}
                onClick={() => {
                  setIsDescriptionExpanded(!isDescriptionExpanded);
                }}
                style={{ cursor: isDescriptionOverflowing ? 'pointer' : 'default', position: 'relative' }}
              >
                <p 
                  ref={descriptionRef}
                  className={`profile-details__about-text ${!shownDescription.trim() ? 'is-ghost' : ''}`}
                >
                  {shownDescription.trim() || 'Описание отсутствует'}
                </p>
              </div>
            </section>

            {interests.length > 0 && (
              <ProfileTagsCollapse tags={interests} className="profile-tags" />
            )}

            {hasPhotosSection && (
              <section className="profile-details__section profile-photos-section">
                <h2>Фотографии</h2>
                <div className="profile-photos" ref={photosContainerRef}>
                  {visiblePhotos.map((photo, index) => {
                    const isExpandableLastPhoto = !showAllPhotos && hiddenPhotosCount > 0 && index === visiblePhotos.length - 1;
                    const content = (
                      <>
                        <img src={photo} alt={`Фото профиля ${index + 1}`} />
                        {isExpandableLastPhoto && (
                          <>
                            <span className="profile-photos__film" />
                            <span className="profile-photos__more">+{morePhotosLabelCount}</span>
                          </>
                        )}
                      </>
                    );

                    return isExpandableLastPhoto ? (
                      <button
                        className="profile-photos__item profile-photos__item--more"
                        key={`${photo}-${index}`}
                        type="button"
                        onClick={() => setShowAllPhotos(true)}
                        aria-label={`Показать еще ${morePhotosLabelCount} фото`}
                      >
                        {content}
                      </button>
                    ) : (
                      <div 
                        className="profile-photos__item" 
                        key={`${photo}-${index}`}
                        onClick={() => openLightbox(userPhotos, index)}
                        style={{cursor: 'pointer'}}
                      >
                        {content}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </section>
        </section>
      </main>
      
      {/* Lightbox Pop-up Viewer */}
      {lightbox.isOpen && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox}>
            <i className="bi bi-x"></i>
          </button>
          {lightbox.images.length > 1 && (
            <>
              <button 
                className="lightbox-arrow arrow-left" 
                onClick={(e) => { e.stopPropagation(); prevLightboxImage(); }}
              >
                <i className="bi bi-chevron-left"></i>
              </button>
              <button 
                className="lightbox-arrow arrow-right" 
                onClick={(e) => { e.stopPropagation(); nextLightboxImage(); }}
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </>
          )}
          <img 
            src={lightbox.images[lightbox.currentIndex]} 
            alt="fullscreen preview" 
            className="lightbox-image" 
            onClick={(e) => e.stopPropagation()} 
          />
        </div>
      )}

      <Footer />
    </div>
  );
}

